import { BookOpen, Download, FileText, Hash, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@nucleus-commerce/ui/components/command';

import { useDocsSearch } from '../hooks/use-docs-search';

import { MethodBadge } from './method-badge';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** Top-bar search: a trigger button plus the ⌘K palette over guides and endpoints. */
export function DocsSearch() {
  const { t } = useTranslation('docs');
  const { open, setOpen, go, guides, operations } = useDocsSearch();

  const pages = [
    { key: 'reference', icon: BookOpen, href: '/docs/api', label: t('reference.title') },
    { key: 'postman', icon: Download, href: '/docs/postman', label: t('postman.title') },
  ];

  return (
    <>
      <Button
        variant="outline"
        aria-label={t('search.trigger')}
        className="text-muted-foreground h-9 justify-between gap-3 px-3 font-normal shadow-none sm:w-56"
        onClick={() => setOpen(true)}
      >
        <span className="flex items-center gap-2 text-[13px]">
          <Search className="size-4" aria-hidden />
          <span className="hidden sm:inline">{t('search.trigger')}</span>
        </span>
        <kbd className="bg-muted hidden rounded px-1.5 py-0.5 font-sans text-[11px] sm:inline">
          {isMac ? '⌘K' : 'Ctrl K'}
        </kbd>
      </Button>
      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t('search.title')}
        description={t('search.description')}
        className="sm:max-w-xl"
      >
        <CommandInput placeholder={t('search.placeholder')} />
        <CommandList className="max-h-[min(28rem,60svh)]">
          <CommandEmpty>{t('search.empty')}</CommandEmpty>
          <CommandGroup heading={t('search.groups.guides')}>
            {guides.map(({ entry, section }) => (
              <CommandItem
                key={entry.id}
                value={entry.id}
                keywords={[entry.title, entry.subtitle]}
                onSelect={() => go(entry.href)}
              >
                {section ? <Hash aria-hidden /> : <FileText aria-hidden />}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{entry.title}</span>
                  {section ? (
                    <span className="text-muted-foreground truncate text-xs">{entry.subtitle}</span>
                  ) : null}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading={t('search.groups.reference')}>
            {operations.map(({ entry, operation }) => (
              <CommandItem
                key={entry.id}
                value={entry.id}
                keywords={[entry.title, operation.path, operation.id, operation.method]}
                onSelect={() => go(entry.href)}
              >
                <MethodBadge method={operation.method} size="xs" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{entry.title}</span>
                  <span className="text-muted-foreground truncate font-mono text-xs">
                    {operation.path}
                  </span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading={t('search.groups.pages')}>
            {pages.map((page) => (
              <CommandItem
                key={page.key}
                value={`page:${page.key}`}
                keywords={[page.label]}
                onSelect={() => go(page.href)}
              >
                <page.icon aria-hidden />
                {page.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
